<template>
  <div class="report-filters" ref="filtersRoot">
    <v-row :justify="isNarrow && !isMobile ? 'start' : 'center'" :no-gutters="isMobile" :dense="!isMobile" :style="isNarrow && !isMobile ? 'gap: 3px;' : ''">
      <v-col :cols="isMobile ? 12 : isNarrow ? 4 : 2" :style="!isMobile && !isNarrow ? 'max-width: 175px;' : ''">
        <v-select
          dense
          outlined
          :items="reportTypes"
          v-model="reportType"
          :hide-details="!isMobile"
          :label="$t('queues.reports.filters.reportType')"
        ></v-select>
      </v-col>
      <v-col :cols="isMobile ? 12 : false" :style="!isMobile && !isNarrow ? 'max-width: 450px;' : ''">
        <v-select
          dense
          outlined
          :items="availableQueues"
          v-model="queue"
          :label="$t('queues.reports.filters.queue')"
          multiple
          :hide-details="!isMobile"
          clearable
          v-if="reportType !== 'agentDetails'"
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0 && queue && queue.length">
              {{ truncatedQueueDisplay.truncatedText }}
              <span v-if="truncatedQueueDisplay.displayTruncated" class="text-caption text--secondary">
                {{ $t('queues.reports.filters.plusMoreQueues', { count: truncatedQueueDisplay.moreCount }) }}
              </span>
            </span>
          </template>
        </v-select>
        <v-autocomplete
          dense
          outlined
          :items="availableAgents"
          v-model="agent"
          :hide-details="!isMobile"
          :label="$t('queues.reports.filters.agent')"
          v-else
        ></v-autocomplete>
      </v-col>
      <v-col v-if="isNarrow" cols="12" class="pa-0 ma-0" style="height:0"></v-col>
      <v-col :cols="isMobile ? 12 : 2" :style="!isMobile ? 'max-width: 130px;' : ''">
        <v-select
          dense
          outlined
          :items="scopeOptions"
          v-model="scope"
          :hide-details="!isMobile"
          :label="$t('queues.reports.filters.scope')"
        ></v-select>
      </v-col>
      <v-col :cols="isMobile ? 12 : 2" :style="!isMobile ? 'max-width: 150px;' : ''">
        <v-menu
          v-model="startMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              dense
              outlined
              v-model="startDate"
              append-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              :hide-details="!isMobile"
              :label="dateLabel"
              @click:append="on.click"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="startDate"
            @input="startMenu = false"
            :hide-details="!isMobile"
            :max="new Date().toISOString().slice(0, 10)"
            :allowed-dates="scope === 'week' ? allowStartofWeek : null"
            :type="scope === 'month' ? 'month' : 'date'"
            :locale="$i18n.locale"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col v-if="scope === 'custom'" :cols="isMobile ? 12 : 2" :style="!isMobile ? 'max-width: 150px;' : ''">
        <v-menu
          v-model="endMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              dense
              outlined
              v-model="endDate"
              append-icon="mdi-calendar"
              readonly
              :hide-details="!isMobile"
              v-bind="attrs"
              v-on="on"
              :label="$t('queues.reports.filters.endDate')"
              @click:append="on.click"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="endDate"
            @input="endMenu = false"
            :min="startDate"
            :hide-details="!isMobile"
            :max="new Date(new Date(startDate).getTime() + (30 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10)"
            :locale="$i18n.locale"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col :cols="isMobile ? 12 : 2" :style="!isMobile ? 'max-width: 175px;' : ''">
        <v-autocomplete
          dense
          outlined
          :items="timezones"
          v-model="timezone"
          :hide-details="!isMobile"
          :label="$t('queues.reports.filters.timezone')"
        ></v-autocomplete>
      </v-col>
      <v-spacer v-if="isNarrow"></v-spacer>
      <v-col :cols="isMobile ? 3 : 1" :class="isMobile ? 'd-flex justify-center' : 'd-flex justify-end'" style="min-width: 80px;">
        <v-btn @click="submit"
          :small="(this.$vuetify.breakpoint.width > 1260 && this.$vuetify.breakpoint.width < 1400) && !isNarrow"
          :style="(this.$vuetify.breakpoint.width > 1260 && this.$vuetify.breakpoint.width < 1400) && !isNarrow ? 'align-self:center;' : ''"
          :disabled="!submitEnabled">
            {{ $t('queues.reports.filters.submit') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import LocalStorage from 'Store/class/local_storage'

export default {
  name: 'queues-reports-filters',
  data () {
    return {
      reportTypes: [
        { value: 'summary', text: this.$t('queues.reports.reportTypes.queueSummary') },
        { value: 'callDetails', text: this.$t('queues.reports.reportTypes.callDetails') },
        { value: 'agentDetails', text: this.$t('queues.reports.reportTypes.agentDetails') }
      ],
      reportType: 'summary',
      queue: null,
      agent: null,
      startDate: null,
      endDate: null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      startMenu: false,
      endMenu: false,
      scope: 'day',
      componentWidth: 0,
      resizeObserver: null
    }
  },
  mounted () {
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        this.componentWidth = entry.contentRect.width
      }
    })
    this.resizeObserver.observe(this.$refs.filtersRoot)
    // load initial values from local storage
    const storedData = LocalStorage.getReportFilters()
    if (storedData?.lastExecuted) {
      this.reportType = storedData.type || this.reportType
      this.queue = storedData.queues || this.queue
      this.agent = storedData.agents || this.agent
      this.timezone = storedData.timezone || this.timezone
      if (Date.now() - new Date(storedData.lastExecuted).getTime() <= 24 * 60 * 60 * 1000) {
        this.scope = storedData.range || this.scope
        this.startDate = storedData.start || this.startDate
        this.endDate = storedData.end || this.endDate
      }
    }
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
  watch: {
    scope (newValue) {
      if (newValue !== 'month') {
        this.startDate = new Date().toISOString().slice(0, 10)
      }
    },
    reportType (newValue) {
      if (newValue === 'agentDetails' || newValue === 'callDetails') {
        this.scope = 'day'
      } else {
        this.scope = 'month'
      }
    }
  },
  computed: {
    ...mapGetters({
      queues: 'queues/allQueueNames',
      users: 'users/listAll'
    }),
    isMobile () {
      return this.$vuetify.breakpoint.mobile
    },
    availableQueues () {
      return this.queues.map(queue => {
        return queue.display
      })
    },
    availableAgents () {
      return this.users.map(user => {
        return { text: `${user.name} (${user.exten})`, value: user.exten }
      })
    },
    scopeOptions () {
      const opt = [
        { value: 'day', text: this.$t('queues.reports.scopeOptions.day') },
        { value: 'week', text: this.$t('queues.reports.scopeOptions.week') }
      ]
      if (this.reportType === 'summary') {
        opt.push({ value: 'month', text: this.$t('queues.reports.scopeOptions.month') })
      }
      opt.push({ value: 'custom', text: this.$t('queues.reports.scopeOptions.custom') })
      return opt
    },
    dateLabel () {
      if (this.scope === 'month') return this.$t('queues.reports.filters.month')
      if (this.scope === 'week') return this.$t('queues.reports.filters.week')
      if (this.scope === 'day') return this.$t('queues.reports.filters.day')
      return this.$t('queues.reports.filters.startDate')
    },
    timezones () {
      return Intl.supportedValuesOf('timeZone')
    },
    submitEnabled () {
      if (!this.reportType || !this.scope) return false
      if (this.reportType !== 'agentDetails' && !this.queue?.length) return false
      if (this.reportType === 'agentDetails' && !this.agent) return false
      if (this.scope === 'custom') {
        if (!this.startDate || !this.endDate) return false
        if (new Date(this.startDate) > new Date(this.endDate)) return false
        if (new Date(this.endDate) > new Date(this.startDate).getTime() + (30 * 24 * 60 * 60 * 1000)) return false
      } else {
        if (!this.startDate) return false
      }
      return true
    },
    truncatedQueueDisplay () {
      if (!this.queue || !this.queue.length) return { truncatedText: '', displayTruncated: false, moreCount: 0 }
      const names = this.queue
      let total = 0
      const result = []
      let moreCount = 0
      for (let i = 0; i < names.length; i++) {
        let name = names[i]
        if ((total + name.length + (i > 0 ? 2 : 0)) > this.visibleQueueThreshold) { // 2 for comma+space
          if (i === 0) {
            name = name.slice(0, this.visibleQueueThreshold - 3) + '...'
          } else {
            moreCount = names.length - i
            break
          }
        }
        result.push(name)
        total += name.length + (i > 0 ? 2 : 0)
      }
      return {
        truncatedText: result.join(', '),
        displayTruncated: moreCount > 0,
        moreCount
      }
    },
    visibleQueueThreshold () {
      return this.scope === 'custom' ? 20 : 40
    },
    isNarrow () {
      return this.componentWidth < 1100
    }
  },
  methods: {
    calculateDates () {
      // Need to calculate start and end dates based on scope
      const baseDate = new Date(this.startDate)
      let firstDay, lastDay
      let hourly = false
      if (this.scope === 'month') {
        firstDay = new Date(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), 1)
        lastDay = new Date(baseDate.getUTCFullYear(), baseDate.getUTCMonth() + 1, 1)
      } else if (this.scope === 'week') {
        firstDay = new Date(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), baseDate.getUTCDate() - baseDate.getUTCDay())
        lastDay = new Date(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), baseDate.getUTCDate() - baseDate.getUTCDay() + 7)
      } else if (this.scope === 'day') {
        firstDay = baseDate
        lastDay = baseDate
        hourly = true
      } else { // Custom range
        firstDay = baseDate
        lastDay = new Date(this.endDate)
      }

      return { firstDay, lastDay, hourly }
    },
    submit () {
      const { firstDay, lastDay, hourly } = this.calculateDates()
      const params = {
        reportType: this.reportType,
        startDate: firstDay.toISOString().slice(0, 10),
        endDate: lastDay.toISOString().slice(0, 10),
        timezone: this.timezone,
        scope: this.scope,
        hourly: hourly,
        breadcrumb_op: 'new'
      }
      if (this.reportType !== 'agentDetails') {
        params.queue = this.queue
      } else {
        params.agentExtension = this.agent
        // Let's search the name of the agent
        const agent = this.users.find(user => user.exten === this.agent)
        params.agentDisplay = agent ? `${agent.name} (${agent.exten})` : this.agent
      }
      this.$emit('refreshData', params)
    },
    allowStartofWeek (value) {
      return new Date(value).getUTCDay() === 0
    }
  }
}
</script>
<style>
  .report-filters .v-input__control,
  .report-filters .v-label,
  .report-filters .v-chip__content {
    font-size: 0.80rem
  }
</style>
